import React, { useState } from "react";
import {
  CannedResponse, Hint, Link, NewCannedResponse, PrimaryBlueButton, Severity, XClose
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import FileUpload from "@/components/FileUpload/FileUpload";
import { getFileExtension } from "@/kb/helpers";
import useToastAlert from "@/hooks/useToastAlert";
import { usePapaParse } from "react-papaparse";
import FilesEditor from "../../../components/FileUpload/FilesEditor";
import useTeams from "@/hooks/useTeams";
import useUsers from "@/hooks/useUsers";
import useApi from "@/hooks/useApi";

interface Props {
  onInport: (responses: CannedResponse[]) => void;
  onClose: () => void;
}

interface CSVRow {
  shortcut: string;
  message: string;
  teams: string;
  agent: string;
}

export default function CannedResponseRightAsideImporter({
  onInport,
  onClose
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [rows, setRows] = useState<CSVRow[] | null>(null);
  const [processingFile, setProcessingFile] = useState(false);
  const [saving, setSaving] = useState(false);

  const api = useApi();
  const { getTeamByName } = useTeams();
  const { getUserByName } = useUsers();
  const { displayToast } = useToastAlert();
  const { readString } = usePapaParse();

  function downloadSampleCSV() {
    const link = document.createElement("a");
    link.href = sampleCSVUrl;
    link.setAttribute("download", "SampleCannedResponseImport.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function onClickImport() {
    if (!rows || !rows.length) {
      displayToast(Severity.Error, "No valid responses to import.");
      return;
    }

    const responses: NewCannedResponse[] = rows.map(createResponseFromRow);

    setSaving(true);
    const response = await api.messaging.post("CannedResponses", { responses });
    setSaving(false);
    if (!response.ok) {
      displayToast(
        Severity.Error,
        "There was a problem saving the canned responses."
      );
      return;
    }

    const data = await response.json();

    onInport(data);
    onClose();
  }

  function createResponseFromRow(row: CSVRow): NewCannedResponse {
    const teamIds = row.teams
      .split(",")
      .map((t) => getTeamByName(t.trim())?.id)
      .filter((t) => t !== undefined);

    const userId = getUserByName(row.agent.trim())?.id;

    //todo: deal with missing teams and agents
    return {
      shortcut: row.shortcut,
      messages: [row.message],
      isGlobal: !row.teams && !row.agent,
      teamIds: teamIds,
      userId: userId
    };
  }

  function addFile(file: File) {
    if (!file) {
      return;
    }

    const extension = getFileExtension(file.name);
    if (extension !== "csv") {
      displayToast(Severity.Error, "Please upload a valid CSV file.");
      return;
    }
    setFile(file);
    setProcessingFile(true);
    setErrors(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      readString<CSVRow>(e.target?.result as string, {
        header: true,
        worker: true,
        skipEmptyLines: "greedy",
        error: (err) => {
          setErrors([err.message]);
          setProcessingFile(false);
        },
        complete: (results) => {
          const errors = results.errors.map(
            (x) => `row ${x.row}: ${x.message}`
          );

          const responses = results.data;

          const validationErrors = validateResponses(responses);
          errors.push(...validationErrors);

          setProcessingFile(false);

          if (errors.length) {
            setErrors(errors);
            return;
          }

          setRows(responses);
        }
      });
    };
    reader.onerror = () => {
      displayToast(Severity.Error, "There was an error reading the file.");
    };
    reader.readAsText(file);
  }

  function validateResponses(responses: CSVRow[]) {
    const errors: string[] = [];

    for (const response of responses) {
      if (response.shortcut === undefined) {
        errors.push("The shortcut column is missing.");
        break;
      }
      if (response.message === undefined) {
        errors.push("The message column is missing.");
        break;
      }
      if (response.teams === undefined) {
        errors.push("The teams column is missing.");
        break;
      }
      if (response.agent === undefined) {
        errors.push("The agent column is missing.");
        break;
      }

      if (!response.shortcut.trim()) {
        errors.push(
          `row ${
            responses.indexOf(response) + 1
          }: The shortcut field cannot be empty.`
        );
      }

      if (!response.message.trim()) {
        errors.push(
          `row ${
            responses.indexOf(response) + 1
          }: The message field cannot be empty.`
        );
      }

      if (response.teams) {
        //make sure the teams exist
        const teamNames = response.teams.split(",").map((t) => t.trim());
        for (const teamName of teamNames) {
          if (!getTeamByName(teamName)) {
            errors.push(
              `row ${
                responses.indexOf(response) + 1
              }: Team "${teamName}" does not exist.`
            );
          }
        }
      }

      if (response.agent) {
        //make sure the agent exists
        if (!getUserByName(response.agent.trim())) {
          errors.push(
            `row ${responses.indexOf(response) + 1}: Agent "${
              response.agent
            }" does not exist.`
          );
        }
      }
    }

    return errors;
  }

  if (saving) {
    return <LoadingSpinner />;
  }

  return (
    <div className="z-40 fixed right-0 top-0 w-1/3 h-full bg-slate-50 text-slate-800 shadow-lg flex flex-col">
      <h2 className="text-lg font-semibold flex items-center justify-between bg-white border-b border-slate-300 p-6">
        Import from File
        <XClose className="text-slate-500 cursor-pointer" onClick={onClose} />
      </h2>
      <div className="p-6 h-full overflow-hidden flex flex-col gap-2">
        <Hint>
          Import your canned responses using the CSV file format. To ensure
          compatibility, please follow our format.
        </Hint>
        <Hint>
          <Link onClick={downloadSampleCSV}>Download sample CSV</Link>
        </Hint>
        {processingFile && <LoadingSpinner />}
        {!processingFile && (
          <>
            <FileUpload
              accept="text/csv"
              onAddFiles={(files) => {
                addFile(files[0]);
              }}
            />
            {file && (
              <>
                <FilesEditor
                  files={[file]}
                  onUpdate={() => {
                    setErrors(null);
                    setFile(null);
                  }}
                />
                {errors && (
                  <span className="text-red-500">
                    {errors.map((error, i) => (
                      <div key={i}>{error}</div>
                    ))}
                  </span>
                )}
                {!errors && rows && (
                  <>
                    <span className="text-green-500">
                      Ready to import {rows.length}{" "}
                      {rows.length === 1 ? "response" : "responses"}.
                    </span>
                    <div className="flex items-end justify-center h-full">
                      <PrimaryBlueButton
                        className="px-14"
                        label="Import"
                        onClick={onClickImport}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const sampleCSVUrl =
  "https://velaromessagingprod.blob.core.windows.net/messaging-public/SampleCannedResponseImport.csv?sv=2023-01-03&spr=https&st=2025-07-22T15%3A26%3A06Z&se=3000-07-23T15%3A26%3A00Z&sr=b&sp=r&sig=0itJwt80B51h81tBrf%2BHjQsEBmnu0iipvmgiRsuvS%2Fc%3D&rscd=attachment";
